import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { formatDistanceToNow, format, parseISO } from "date-fns";
import {
  LayoutDashboard,
  Calendar,
  Users,
  Image as ImageIcon,
  Video,
  Inbox,
  CreditCard,
  GraduationCap,
  UserRoundPlus,
  Radio,
  ClipboardList,
  Target,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  Search,
  Bell,
  X,
  CheckCircle2,
  Sparkles,
  Menu,
  FlaskConical,
  Phone,
  Mail,
  CalendarDays,
  CalendarRange,
  Receipt,
  ShieldCheck,
  GripVertical,
  KeyRound,
  Flame,
  Pencil,
  Save,
  Trash2,
  Wallet,
  Share2,
  LogOut,
  TrendingUp,
  History,
} from "lucide-react";
import { motion } from "framer-motion";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { groupByIdentity, collectEmails, normEmail, normPhone10, normName } from "@/lib/student-dedupe";
import { supabase } from "@/integrations/supabase/client";
import { AdminSearchProvider, useAdminSearch } from "./AdminSearchContext";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { updateStudentDetailsGlobal, deleteStudentRecords } from "@/lib/admin-payment-ops.functions";
import { broadcastAdminDataUpdated } from "@/lib/admin-refresh";
import { readSeenPayments, writeSeenPayments, SEEN_PAYMENTS_EVENT } from "./RecentPaymentsWidget";

type NavGroup = "primary" | "Cohorts" | "Operations" | "Teams" | "Topics";

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  group: NavGroup;
  search?: Record<string, string>;
  matchCategory?: string;
  /** Only visible to the single owner account (see OWNER_EMAIL). */
  ownerOnly?: boolean;
};

/** The only account allowed to see the private Website Changes log. */
export const OWNER_EMAIL = "sriramalla@welivdit.com";


const NAV: NavItem[] = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, group: "primary" },
  { to: "/admin/students", label: "Students", icon: GraduationCap, group: "primary" },
  { to: "/admin/inbox", label: "Student Inbox", icon: Inbox, group: "primary" },
  { to: "/admin/interview-track", label: "Interview Track", icon: Target, group: "primary" },

  { to: "/admin/interview-priority", label: "Interview Priority", icon: Flame, group: "Operations" },
  { to: "/admin/mock-scheduling", label: "📅 Schedule & DS-160", icon: CalendarDays, group: "Operations" },
  { to: "/admin/leads", label: "Leads", icon: Inbox, group: "Operations" },
  { to: "/admin/signups", label: "Signups", icon: UserRoundPlus, group: "Operations" },
  { to: "/admin/incomplete-checkouts", label: "Incomplete Checkouts", icon: Users, group: "Operations" },
  {
    to: "/admin/payments",
    label: "Mock Payments",
    icon: CreditCard,
    group: "Operations",
    search: { category: "mocks" },
    matchCategory: "mocks",
  },
  {
    to: "/admin/payments",
    label: "Visa Readiness Payments",
    icon: Sparkles,
    group: "Operations",
    search: { category: "readiness" },
    matchCategory: "readiness",
  },
  { to: "/admin/pending-payments", label: "Pending Payments", icon: Wallet, group: "Operations" },
  { to: "/admin/intake", label: "Client Intake", icon: ClipboardList, group: "Operations" },

  { to: "/admin/monthly-enrollments", label: "Enrollments & Approvals", icon: CalendarRange, group: "Teams" },
  { to: "/admin/cohorts/fall-2026", label: "Fall 2026 Cohort", icon: Users, group: "Cohorts" },
  { to: "/admin/cohorts/spring-2027", label: "Spring 2027 Cohort", icon: Users, group: "Cohorts" },
  { to: "/admin/cohorts/fall-2027", label: "Fall 2027 Cohort", icon: Users, group: "Cohorts" },
  { to: "/admin/analytics", label: "Analytics & Growth", icon: TrendingUp, group: "Teams" },
  { to: "/admin/access", label: "Admin Access", icon: KeyRound, group: "Teams" },
  { to: "/admin/unsubscribe", label: "Unsubscribes", icon: Mail, group: "Teams" },
  { to: "/admin/logs", label: "Email Activity", icon: Mail, group: "Teams" },
  { to: "/admin/live-sessions", label: "Live Sessions", icon: Radio, group: "Teams" },
  { to: "/admin/visa-assessments", label: "Visa Assessments", icon: Sparkles, group: "Teams" },
  { to: "/admin/visa-testers", label: "Visa Readiness Testers", icon: Users, group: "Teams" },

  { to: "/admin/upload-experience", label: "Upload Experience", icon: ImageIcon, group: "Topics" },
  { to: "/admin/broadcast", label: "Broadcast Message", icon: Radio, group: "Topics" },
  { to: "/admin/social-preview", label: "Social Preview", icon: Share2, group: "Topics" },
  { to: "/admin/testing", label: "Email Sandbox", icon: FlaskConical, group: "Topics" },
  { to: "/admin/settings", label: "Developer Tools", icon: FlaskConical, group: "Topics" },
  { to: "/admin/changes", label: "Website Changes", icon: History, group: "Topics", ownerOnly: true },
];

const COLLAPSIBLE_GROUPS: Exclude<NavGroup, "primary">[] = ["Cohorts", "Operations", "Teams", "Topics"];


type PaymentEntry = {
  id: string;
  amount: number;
  date: string | null;
  plan: string | null;
  source: "razorpay" | "manual";
  status: "paid" | "failed" | "created";
};

const PAYMENT_STATUS_META: Record<
  PaymentEntry["status"],
  { label: string; className: string }
> = {
  paid: {
    label: "🟢 Paid",
    className: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300",
  },
  failed: {
    label: "🔴 Failed",
    className: "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300",
  },
  created: {
    label: "⏳ Initiated / Pending",
    className: "bg-muted text-muted-foreground",
  },
};

const normalizePaymentStatus = (s: string | null | undefined): PaymentEntry["status"] =>
  s === "paid" ? "paid" : s === "failed" ? "failed" : "created";


type StudentSearchResult = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  plan: string | null;
  interviewDate: string | null;
  biometricsDate: string | null;
  consulate: string | null;
  payments: PaymentEntry[];
  hasIntake: boolean;
  /** Every email known for this student (primary first, then alternates). */
  emails: string[];
};

const isManualOrder = (id: string | null | undefined) => !id || id.toLowerCase().startsWith("manual");


type ExperienceHit = { id: string; display_name: string; consulate_location: string | null; interview_date: string | null };
type QueryHit = { id: string; page_slug: string; author_name: string | null; content: string; created_at: string };


type StudentEditPatch = Partial<
  Pick<StudentSearchResult, "name" | "email" | "phone" | "plan" | "interviewDate" | "biometricsDate">
>;

/** Inline editor for a student's core record, synced across every admin table. */
function StudentEditForm({
  student,
  onSaved,
  onCancel,
}: {
  student: StudentSearchResult;
  onSaved: (patch: StudentEditPatch) => void;
  onCancel: () => void;
}) {
  const save = useServerFn(updateStudentDetailsGlobal);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fullName: student.name ?? "",
    email: student.email ?? "",
    phone: student.phone ?? "",
    biometricsDate: student.biometricsDate ?? "",
    visaDate: student.interviewDate ? student.interviewDate.slice(0, 10) : "",
    plan: student.plan ?? "",
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await save({
        data: {
          matchEmail: student.email,
          matchPhone: student.phone,
          matchPaymentId: student.payments[0]?.id ?? null,
          fullName: form.fullName.trim(),
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          biometricsDate: form.biometricsDate || null,
          visaDate: form.visaDate || null,
          plan: form.plan.trim() || null,
        },
      });
      onSaved({
        name: form.fullName.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        plan: form.plan.trim() || null,
        interviewDate: form.visaDate || null,
        biometricsDate: form.biometricsDate || null,
      });
      broadcastAdminDataUpdated();
      toast.success("Student details updated & synced globally");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't update details");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs outline-none focus:border-primary";
  const label = "mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground";

  return (
    <form onSubmit={submit} className="mt-3 grid gap-3 rounded-xl border border-primary/30 bg-background/60 p-3 sm:grid-cols-2">
      <div>
        <label className={label} htmlFor={`n-${student.id}`}>Full name</label>
        <input id={`n-${student.id}`} className={field} value={form.fullName} onChange={set("fullName")} required />
      </div>
      <div>
        <label className={label} htmlFor={`e-${student.id}`}>Email address</label>
        <input id={`e-${student.id}`} type="email" className={field} value={form.email} onChange={set("email")} />
      </div>
      <div>
        <label className={label} htmlFor={`p-${student.id}`}>Mobile / WhatsApp</label>
        <input id={`p-${student.id}`} className={field} value={form.phone} onChange={set("phone")} />
      </div>
      <div>
        <label className={label} htmlFor={`pl-${student.id}`}>Support tier / plan</label>
        <input id={`pl-${student.id}`} className={field} value={form.plan} onChange={set("plan")} placeholder="Full Support" />
      </div>
      <div>
        <label className={label} htmlFor={`b-${student.id}`}>Biometric date</label>
        <input id={`b-${student.id}`} type="date" className={field} value={form.biometricsDate} onChange={set("biometricsDate")} />
      </div>
      <div>
        <label className={label} htmlFor={`v-${student.id}`}>Visa interview date</label>
        <input id={`v-${student.id}`} type="date" className={field} value={form.visaDate} onChange={set("visaDate")} />
      </div>
      <div className="flex items-center gap-2 sm:col-span-2">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground disabled:opacity-60"
        >
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save & sync globally"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium">
          Cancel
        </button>
      </div>
    </form>
  );
}

export function GlobalSearchOverlay() {
  const { query, clear } = useAdminSearch();
  const [results, setResults] = useState<StudentSearchResult[]>([]);
  const [experiences, setExperiences] = useState<ExperienceHit[]>([]);
  const [queries, setQueries] = useState<QueryHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const removeRecords = useServerFn(deleteStudentRecords);
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);

  /** Purges the student's whole footprint, or a single duplicate payment row. */
  const handleDelete = async (s: StudentSearchResult, paymentId?: string) => {
    const label = paymentId ? "this duplicate payment record" : `ALL records for ${s.name}`;
    if (!window.confirm(`Permanently delete ${label}? This cannot be undone.`)) return;
    setDeletingId(paymentId ?? s.id);
    try {
      const res = await removeRecords({
        data: paymentId
          ? { paymentIds: [paymentId] }
          : { purgeStudent: true, matchEmail: s.email, matchPhone: s.phone },
      });
      if (paymentId) {
        setResults((prev) =>
          prev.map((r) => (r.id === s.id ? { ...r, payments: r.payments.filter((p) => p.id !== paymentId) } : r)),
        );
        toast.success("Duplicate payment record deleted");
      } else {
        setResults((prev) => prev.filter((r) => r.id !== s.id));
        toast.success(
          `Deleted ${res.paymentsDeleted} payment record(s) and ${res.intakesDeleted} intake record(s)`,
        );
      }
      broadcastAdminDataUpdated();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't delete record");
    } finally {
      setDeletingId(null);
    }
  };


  // Escape always dismisses the overlay and resets the query state.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        clear();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [clear]);

  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }

    let active = true;
    setLoading(true);

    const searchAll = async () => {
      const like = q.replace(/[%,()]/g, "");
      const paySelect =
        "id, buyer_name, buyer_email, buyer_phone, plan_name, amount_paise, created_at, interview_date, razorpay_order_id, status";
      const intakeSelect = "id, full_name, email, whatsapp, plan, visa_date, biometrics_date, visa_city";

      const [{ data: payments }, { data: intakes }, { data: exps }, { data: qs }] = await Promise.all([
        supabase
          .from("payments")
          .select(paySelect)
          .or(`buyer_name.ilike.%${like}%,buyer_phone.ilike.%${like}%,buyer_email.ilike.%${like}%`)
          .limit(30),
        supabase
          .from("client_intakes")
          .select(intakeSelect)
          .or(`full_name.ilike.%${like}%,whatsapp.ilike.%${like}%,email.ilike.%${like}%`)
          .limit(30),
        supabase
          .from("interview_experiences")
          .select("id, display_name, consulate_location, interview_date")
          .or(`display_name.ilike.%${like}%,consulate_location.ilike.%${like}%,university_name.ilike.%${like}%`)
          .limit(6),
        supabase
          .from("guide_questions")
          .select("id, page_slug, author_name, content, created_at")
          .or(`content.ilike.%${like}%,author_name.ilike.%${like}%`)
          .order("created_at", { ascending: false })
          .limit(6),
      ]);

      if (!active) return;

      type PayRow = NonNullable<typeof payments>[number];
      type IntakeRow = NonNullable<typeof intakes>[number];

      // Second pass: the first query only matched the typed email, so rows that
      // belong to the SAME student under a different email were missed. Widen
      // by the phone numbers and names we just discovered.
      const phones = Array.from(
        new Set([
          ...(payments ?? []).map((p) => normPhone10(p.buyer_phone)),
          ...(intakes ?? []).map((i) => normPhone10(i.whatsapp)),
        ]),
      ).filter(Boolean);
      const names = Array.from(
        new Set([
          ...(payments ?? []).map((p) => (p.buyer_name ?? "").trim()),
          ...(intakes ?? []).map((i) => (i.full_name ?? "").trim()),
        ]),
      ).filter((n) => n.length > 2 && normName(n));

      let extraPayments: PayRow[] = [];
      let extraIntakes: IntakeRow[] = [];
      if (phones.length || names.length) {
        const orParts = [
          ...phones.slice(0, 8).map((d) => `buyer_phone.ilike.%${d}%`),
          ...names.slice(0, 8).map((n) => `buyer_name.ilike.${n.replace(/[%,()]/g, "")}`),
        ].join(",");
        const orPartsIntake = [
          ...phones.slice(0, 8).map((d) => `whatsapp.ilike.%${d}%`),
          ...names.slice(0, 8).map((n) => `full_name.ilike.${n.replace(/[%,()]/g, "")}`),
        ].join(",");
        const [p2, i2] = await Promise.all([
          supabase.from("payments").select(paySelect).or(orParts).limit(60),
          supabase.from("client_intakes").select(intakeSelect).or(orPartsIntake).limit(60),
        ]);
        if (!active) return;
        extraPayments = (p2.data ?? []) as PayRow[];
        extraIntakes = (i2.data ?? []) as IntakeRow[];
      }

      const allPayments = Array.from(
        new Map([...(payments ?? []), ...extraPayments].map((r) => [r.id, r])).values(),
      );
      const allIntakes = Array.from(
        new Map([...(intakes ?? []), ...extraIntakes].map((r) => [r.id, r])).values(),
      );

      // Union-find across email / 10-digit phone / exact name over both sources.
      type Unified = { kind: "payment"; row: PayRow } | { kind: "intake"; row: IntakeRow };
      const unified: Unified[] = [
        ...allPayments.map((row) => ({ kind: "payment" as const, row })),
        ...allIntakes.map((row) => ({ kind: "intake" as const, row })),
      ];

      const clusters = groupByIdentity(unified, (u) =>
        u.kind === "payment"
          ? { email: u.row.buyer_email, phone: u.row.buyer_phone, name: u.row.buyer_name, fallbackId: u.row.id }
          : { email: u.row.email, phone: u.row.whatsapp, name: u.row.full_name, fallbackId: u.row.id },
      );

      const built: StudentSearchResult[] = [];
      for (const members of clusters) {
        const pays = members.filter((m) => m.kind === "payment").map((m) => m.row as PayRow);
        const forms = members.filter((m) => m.kind === "intake").map((m) => m.row as IntakeRow);
        if (!pays.length && !forms.length) continue;

        // Razorpay is the source of truth for payment identity when present.
        const gateway = pays.find((p) => !isManualOrder(p.razorpay_order_id));
        const primaryPay = gateway ?? pays[0];
        const primaryForm = forms[0];

        const emails = collectEmails([
          primaryPay?.buyer_email,
          ...pays.map((p) => p.buyer_email),
          ...forms.map((f) => f.email),
        ]);

        const entries: PaymentEntry[] = pays
          .map((p) => ({
            id: p.id,
            amount: p.amount_paise ? p.amount_paise / 100 : 0,
            date: p.created_at,
            plan: p.plan_name,
            source: isManualOrder(p.razorpay_order_id) ? ("manual" as const) : ("razorpay" as const),
            status: normalizePaymentStatus(p.status),
          }))
          .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));

        built.push({
          id: `s:${primaryPay?.id ?? primaryForm?.id ?? emails[0] ?? Math.random().toString(36).slice(2)}`,
          name: primaryPay?.buyer_name || primaryForm?.full_name || "Student",
          email: emails[0] ?? null,
          emails,
          phone: primaryPay?.buyer_phone ?? primaryForm?.whatsapp ?? null,
          plan: primaryPay?.plan_name ?? primaryForm?.plan ?? null,
          interviewDate: primaryPay?.interview_date ?? primaryForm?.visa_date ?? null,
          biometricsDate: primaryForm?.biometrics_date ?? null,
          consulate: primaryForm?.visa_city ?? null,
          payments: entries,
          hasIntake: forms.length > 0,
        });
      }

      // Only surface students that actually match the typed query on any of
      // their indexed identities (including secondary emails).
      const matches = built.filter((r) => {
        const hay = [
          r.name,
          r.phone ?? "",
          ...r.emails,
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(q) || r.emails.some((e) => normEmail(e) === q);
      });

      setResults(matches.slice(0, 12));
      setExperiences((exps ?? []) as ExperienceHit[]);
      setQueries((qs ?? []) as QueryHit[]);
      setLoading(false);
    };

    const timer = setTimeout(searchAll, 200);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    if (!query) setPos({ x: 0, y: 0 });
  }, [query]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, originX: pos.x, originY: pos.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    setPos({ x: d.originX + (e.clientX - d.startX), y: d.originY + (e.clientY - d.startY) });
  };
  const onPointerUp = () => {
    dragRef.current = null;
  };

  if (!query || query.trim().length < 2) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Dark blurred backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15 }}
        onClick={clear}
        aria-hidden
        className="absolute inset-0 bg-black/50 backdrop-blur-md"
      />

      {/* Floating draggable window */}
      <motion.div
        initial={{ opacity: 0, y: -12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
        role="dialog"
        aria-label="Universal search"
        className="absolute left-1/2 top-24 w-[min(42rem,calc(100vw-2rem))] -translate-x-1/2 rounded-2xl border border-border bg-card shadow-2xl"
        style={{ transform: `translate(calc(-50% + ${pos.x}px), ${pos.y}px)` }}
      >
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className="flex cursor-grab items-center justify-between gap-3 rounded-t-2xl border-b border-border/60 bg-muted/40 px-4 py-3 active:cursor-grabbing"
        >
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            <GripVertical className="h-3.5 w-3.5" /> Universal Search — "{query}"
          </p>
          <button
            onClick={clear}
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" /> Close
          </button>
        </div>

        <div className="max-h-[65vh] overflow-y-auto p-4">
          {loading ? (
            <p className="py-6 text-center text-xs text-muted-foreground">Searching records...</p>
          ) : results.length === 0 && experiences.length === 0 && queries.length === 0 ? (
            <p className="py-6 text-center text-xs text-muted-foreground">No records found matching "{query}"</p>
          ) : (
            <div className="space-y-3">
              {results.length > 0 && (
                <p className="px-1 text-[10px] font-black uppercase tracking-wider text-muted-foreground">
                  Students &amp; Clients
                </p>
              )}
              {results.map((s) => (
                <div key={s.id} className="rounded-xl border border-border/60 bg-muted/20 p-4 transition hover:border-primary/50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h4 className="flex items-center gap-2 text-sm font-bold text-foreground">
                        {s.name}
                        {s.hasIntake && (
                          <span className="rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] font-bold text-violet-600 dark:text-violet-300">
                            Intake Form Completed
                          </span>
                        )}
                      </h4>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        {s.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3 text-primary" /> {s.phone}
                          </span>
                        )}
                        {s.emails.map((em, i) => (
                          <span key={em} className="flex items-center gap-1 break-all">
                            <Mail className="h-3 w-3 text-primary" /> {em}
                            {i > 0 && <span className="text-[10px] uppercase opacity-70">alt</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[10px] font-bold text-primary">
                        {s.plan || "No plan"}
                      </span>
                      <button
                        type="button"
                        onClick={() => setEditingId(editingId === s.id ? null : s.id)}
                        aria-label={`Edit details for ${s.name}`}
                        className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[10px] font-bold text-muted-foreground transition hover:border-primary/60 hover:text-primary"
                      >
                        <Pencil className="h-3 w-3" /> Edit Details
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(s)}
                        disabled={deletingId === s.id}
                        aria-label={`Delete all records for ${s.name}`}
                        className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/5 px-2.5 py-1 text-[10px] font-bold text-rose-600 transition hover:bg-rose-500/15 disabled:opacity-50 dark:text-rose-300"
                      >
                        <Trash2 className="h-3 w-3" /> {deletingId === s.id ? "Deleting…" : "Delete Record"}
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 border-t border-border/40 pt-3">
                    <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-muted-foreground">
                      <Receipt className="h-3.5 w-3.5 text-emerald-600" /> Payment Details
                    </p>
                    {s.payments.length === 0 ? (
                      <p className="mt-1 text-xs text-muted-foreground">No transactions recorded</p>
                    ) : (
                      <ul className="mt-1.5 space-y-1.5">
                        {s.payments.map((p) => (
                          <li key={p.id} className="flex flex-wrap items-center gap-2 text-xs">
                            <span
                              className={cn(
                                "rounded-full px-2 py-0.5 text-[10px] font-bold",
                                PAYMENT_STATUS_META[p.status].className,
                              )}
                            >
                              {PAYMENT_STATUS_META[p.status].label}
                            </span>
                            <span

                              className={cn(
                                "rounded-full px-2 py-0.5 text-[10px] font-bold",
                                p.source === "razorpay"
                                  ? "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300"
                                  : "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
                              )}
                            >
                              {p.source === "razorpay" ? "⚡ Razorpay" : "📝 Manual"}
                            </span>
                            <span className="font-semibold tabular-nums">₹{p.amount.toLocaleString("en-IN")}</span>
                            <span className="text-muted-foreground tabular-nums">
                              {p.date ? format(parseISO(p.date), "dd MMM yyyy") : "N/A"}
                            </span>
                            {p.plan && <span className="text-muted-foreground">· {p.plan}</span>}
                            <button
                              type="button"
                              onClick={() => handleDelete(s, p.id)}
                              disabled={deletingId === p.id}
                              aria-label="Delete this duplicate payment record"
                              className="ml-auto inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] font-bold text-muted-foreground transition hover:border-rose-500/50 hover:text-rose-600 disabled:opacity-50"
                            >
                              <Trash2 className="h-3 w-3" /> {deletingId === p.id ? "Deleting…" : "Delete"}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-border/40 pt-3 text-xs">
                    <span className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-amber-600" />
                      <span>
                        <span className="block text-[10px] font-bold uppercase text-muted-foreground">
                          Interview Date
                        </span>
                        <span className="font-semibold">
                          {s.interviewDate ? format(parseISO(s.interviewDate), "dd MMM yyyy") : "Not Set"}
                        </span>
                      </span>
                    </span>
                    {s.consulate && (
                      <span className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-primary" />
                        <span>
                          <span className="block text-[10px] font-bold uppercase text-muted-foreground">Consulate</span>
                          <span className="font-semibold">{s.consulate}</span>
                        </span>
                      </span>
                    )}
                  </div>

                  {editingId === s.id && (
                    <StudentEditForm
                      student={s}
                      onCancel={() => setEditingId(null)}
                      onSaved={(patch) => {
                        setResults((prev) =>
                          prev.map((r) => (r.id === s.id ? { ...r, ...patch } : r)),
                        );
                        setEditingId(null);
                      }}
                    />
                  )}
                </div>
              ))}

              {experiences.length > 0 && (
                <>
                  <p className="px-1 pt-2 text-[10px] font-black uppercase tracking-wider text-muted-foreground">
                    Visa Experiences
                  </p>
                  {experiences.map((e) => (
                    <Link
                      key={e.id}
                      to="/admin/upload-experience"
                      onClick={clear}
                      className="block rounded-xl border border-border/60 bg-muted/20 p-3 text-xs transition hover:border-primary/50"
                    >
                      <span className="font-bold text-foreground">{e.display_name}</span>
                      <span className="ml-2 text-muted-foreground">
                        {e.consulate_location ?? "Consulate N/A"}
                        {e.interview_date ? ` · ${e.interview_date}` : ""}
                      </span>
                    </Link>
                  ))}
                </>
              )}

              {queries.length > 0 && (
                <>
                  <p className="px-1 pt-2 text-[10px] font-black uppercase tracking-wider text-muted-foreground">
                    Q&amp;A Queries
                  </p>
                  {queries.map((q) => (
                    <div
                      key={q.id}
                      className="rounded-xl border border-border/60 bg-muted/20 p-3 text-xs transition hover:border-primary/50"
                    >
                      <p className="font-bold text-foreground">{q.author_name || "Student"}</p>
                      <p className="mt-1 line-clamp-2 text-muted-foreground">{q.content}</p>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}


export function AdminShell({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const searchCategory = useRouterState({
    select: (r) => (r.location.search as { category?: string } | undefined)?.category,
  });
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname, searchCategory]);

  return (
    <AdminSearchProvider>
      <div className="admin-surface min-h-screen bg-[#F7F4F1] font-sans text-foreground dark:bg-zinc-950">
        <div className="mx-auto flex w-full max-w-[1600px] gap-6 px-4 py-5 sm:px-6 lg:px-8">
          {/* Desktop Sidebar */}
          <aside
            className={cn(
              "sticky top-5 hidden h-[calc(100vh-2.5rem)] shrink-0 flex-col rounded-2xl border border-border/80 bg-card/90 shadow-sm transition-all duration-200 ease-in-out lg:flex",
              collapsed ? "w-18" : "w-64",
            )}
            aria-label="Admin navigation"
          >
            <SidebarInner
              collapsed={collapsed}
              onToggleCollapse={() => setCollapsed((v) => !v)}
              pathname={pathname}
              searchCategory={searchCategory}
            />
          </aside>

          {/* Mobile Drawer */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent side="left" className="w-72 p-0 lg:hidden">
              <div className="flex h-full flex-col bg-card">
                <SidebarInner
                  collapsed={false}
                  pathname={pathname}
                  searchCategory={searchCategory}
                  onNavigate={() => setMobileOpen(false)}
                />
              </div>
            </SheetContent>
          </Sheet>

          {/* Main Content Area */}
          <div className="min-w-0 flex-1">
            {/* Top Navigation Bar */}
            <header className="sticky top-5 z-30 mb-6 flex items-center justify-between gap-4 rounded-2xl border border-border/80 bg-card/95 px-4 py-2.5 shadow-sm backdrop-blur-md">
              <div className="flex flex-1 items-center gap-3">
                <button
                  type="button"
                  onClick={() => setMobileOpen(true)}
                  aria-label="Open menu"
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-border text-muted-foreground hover:bg-muted lg:hidden"
                >
                  <Menu className="h-5 w-5" />
                </button>
                <AdminSearchInput />
              </div>

              <div className="flex items-center gap-3">
                <div className="hidden items-center gap-2 rounded-xl bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary border border-primary/20 sm:flex">
                  <ShieldCheck className="h-4 w-4" />
                  <span>Admin Verified</span>
                </div>
              </div>
            </header>

            {/* Global Universal Search Popup */}
            <GlobalSearchOverlay />

            {/* Page Header */}
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0">
                <h1 className="truncate text-2xl font-bold tracking-tight text-foreground sm:text-3xl">{title}</h1>
                {description && <p className="mt-1 text-xs font-medium text-muted-foreground">{description}</p>}
              </div>
              {actions && <div className="flex shrink-0 items-center gap-2.5">{actions}</div>}
            </div>

            {children}
          </div>
        </div>
      </div>
    </AdminSearchProvider>
  );
}

function NavLinkRow({
  item,
  active,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  return (
    <li>
      <Link
        to={item.to}
        search={item.search as never}
        onClick={onNavigate}
        className={cn(
          "flex items-center gap-3 rounded-lg px-2.5 py-[7px] text-[13px] transition-colors",
          active
            ? "bg-muted font-semibold text-foreground"
            : "font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground",
          collapsed && "justify-center px-2",
        )}
        title={collapsed ? item.label : undefined}
      >
        <Icon className={cn("h-[18px] w-[18px] shrink-0", active ? "text-foreground" : "text-muted-foreground")} />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </Link>
    </li>
  );
}

function SidebarInner({
  collapsed,
  onToggleCollapse,
  onNavigate,
  pathname,
  searchCategory,
}: {
  collapsed: boolean;
  onToggleCollapse?: () => void;
  onNavigate?: () => void;
  pathname: string;
  searchCategory: string | undefined;
}) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    Operations: true,
    Teams: false,
    Topics: false,
  });

  const [isOwner, setIsOwner] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      const em = data.session?.user?.email?.toLowerCase() ?? "";
      if (!cancelled) setIsOwner(em === OWNER_EMAIL);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleNav = useMemo(() => NAV.filter((n) => !n.ownerOnly || isOwner), [isOwner]);

  const isActive = (item: NavItem) => {
    const pathMatches =
      item.to === "/admin" ? pathname === "/admin" : pathname === item.to || pathname.startsWith(item.to + "/");
    if (pathMatches && item.to === "/admin/payments") {
      return (searchCategory ?? "mocks") === (item.matchCategory ?? "mocks");
    }
    return pathMatches;
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2 px-4 pt-4">
        {!collapsed && (
          <Link
            to="/admin"
            onClick={onNavigate}
            className="inline-flex min-w-0 items-center gap-2.5 text-sm font-bold tracking-tight text-foreground"
          >
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary text-xs font-black text-primary-foreground">
              W
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-[13px] font-semibold leading-none">WeLivdIt Admin</span>
              <span className="mt-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Console
              </span>
            </div>
          </Link>
        )}
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            className="ml-auto grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground transition hover:bg-muted"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
          </button>
        )}
      </div>

      {/* Minimal ⌘K search box (blocks.so style) */}
      {!collapsed && (
        <div className="px-3 pb-2 pt-3">
          <button
            type="button"
            onClick={() => {
              document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }));
              window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));
            }}
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-muted-foreground transition hover:bg-muted/60"
          >
            <Search className="h-[18px] w-[18px]" />
            <span className="flex-1 text-left">Search</span>
            <kbd className="rounded-md border border-border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              ⌘K
            </kbd>
          </button>
        </div>
      )}

      <nav className="no-scrollbar flex-1 overflow-y-auto px-3 pb-3">
        <ul className="flex flex-col gap-0.5">
          {visibleNav.filter((n) => n.group === "primary").map((item) => (
            <NavLinkRow
              key={`${item.to}:${item.label}`}
              item={item}
              active={isActive(item)}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          ))}
        </ul>

        {COLLAPSIBLE_GROUPS.map((group) => {
          const items = visibleNav.filter((n) => n.group === group);
          const open = collapsed ? true : (openGroups[group] ?? group === "Cohorts");
          return (
            <div key={group} className="mt-5">
              {!collapsed && (
                <button
                  type="button"
                  onClick={() => setOpenGroups((g) => ({ ...g, [group]: !open }))}
                  className="flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-muted-foreground transition hover:text-foreground"
                  aria-expanded={open}
                >
                  <span>{group}</span>
                  <ChevronDown className={cn("h-4 w-4 transition-transform", open ? "" : "-rotate-90")} />
                </button>
              )}
              {open && (
                <ul className="mt-0.5 flex flex-col gap-0.5">
                  {items.map((item) => (
                    <NavLinkRow
                      key={`${item.to}:${item.label}`}
                      item={item}
                      active={isActive(item)}
                      collapsed={collapsed}
                      onNavigate={onNavigate}
                    />
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      <SidebarProfile collapsed={collapsed} />
    </>
  );
}

/** Bottom panel: signed-in admin identity, alert badge + logout. */
function SidebarProfile({ collapsed }: { collapsed: boolean }) {
  const [email, setEmail] = useState<string | null>(null);
  const [alerts, setAlerts] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setEmail(data.session?.user?.email ?? null);
    });
    void supabase
      .from("payments")
      .select("id", { count: "exact", head: true })
      .eq("status", "paid")
      .gte("created_at", new Date(Date.now() - 7 * 864e5).toISOString())
      .then(({ count }) => {
        if (!cancelled) setAlerts(count ?? 0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.href = "/auth";
  };

  return (
    <div className="border-t border-border/50 p-3">
      <div className={cn("flex items-center gap-2.5", collapsed && "justify-center")}>
        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-[11px] font-bold uppercase text-primary">
          {(email ?? "?").slice(0, 2)}
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-semibold text-foreground">{email ?? "Signed in"}</p>
            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Administrator</p>
          </div>
        )}
        {!collapsed && alerts > 0 && (
          <span className="grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
            {alerts > 99 ? "99+" : alerts}
          </span>
        )}
        <button
          type="button"
          onClick={() => void signOut()}
          aria-label="Log out"
          title="Log out — Admin Verified"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}


function AdminSearchInput() {
  const { query, setQuery, clear } = useAdminSearch();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="relative flex-1 max-w-md">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            clear();
            inputRef.current?.blur();
          }
        }}
        placeholder="Search students, experiences, queries..."
        className="h-9 w-full rounded-xl border border-border/80 bg-muted/30 pl-9 pr-16 text-xs font-medium text-foreground placeholder:text-muted-foreground focus:border-primary focus:bg-card focus:outline-none focus:ring-1 focus:ring-primary/20 transition"
      />
      {query ? (
        <button
          type="button"
          onClick={clear}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-lg text-muted-foreground hover:bg-muted"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : (
        <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded-md border border-border bg-card px-1.5 py-0.5 font-sans text-[10px] font-bold text-muted-foreground">
          ⌘K
        </kbd>
      )}
    </div>
  );
}

type RecentPayment = {
  id: string;
  buyer_name: string;
  plan_name: string;
  amount_paise: number;
  created_at: string;
};

function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<RecentPayment[] | null>(null);
  const [seenIds, setSeenIds] = useState<Set<string>>(new Set());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSeenIds(readSeenPayments());
    const sync = () => setSeenIds(readSeenPayments());
    window.addEventListener(SEEN_PAYMENTS_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SEEN_PAYMENTS_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const { data } = await supabase
        .from("payments")
        .select("id, buyer_name, plan_name, amount_paise, created_at")
        .eq("status", "paid")
        .order("created_at", { ascending: false })
        .limit(10);
      if (active) setItems((data ?? []) as RecentPayment[]);
    };
    load();
    // Near-live: poll every 10s, and instantly when the tab regains focus.
    const t = setInterval(load, 10_000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      active = false;
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  // Toast when a brand-new payment arrives while the admin is on any page.
  const knownRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!items) return;
    if (knownRef.current) {
      for (const p of items) {
        if (!knownRef.current.has(p.id)) {
          toast.success(`New payment: ${p.buyer_name} · ${p.plan_name} · ₹${Math.round((p.amount_paise || 0) / 100).toLocaleString("en-IN")}`);
        }
      }
    }
    knownRef.current = new Set(items.map((p) => p.id));
  }, [items]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const unread = (items ?? []).filter((p) => !seenIds.has(p.id));
  const unreadCount = unread.length;

  const markAllSeen = () => {
    const next = new Set(seenIds);
    (items ?? []).forEach((p) => next.add(p.id));
    setSeenIds(next);
    writeSeenPayments(next);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) markAllSeen();
        }}
        className="relative grid h-9 w-9 place-items-center rounded-xl border border-border/80 bg-card text-muted-foreground shadow-sm hover:bg-muted transition"
        aria-label={`Notifications${unreadCount ? ` (${unreadCount} new)` : ""}`}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 grid h-4 min-w-[1rem] place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground shadow-sm">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-40 w-80 overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
          <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-foreground">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" /> Live Payments
            </p>
            <Link
              to="/admin/payments"
              search={{ category: "mocks" as const }}
              onClick={() => setOpen(false)}
              className="text-xs font-semibold text-primary hover:underline"
            >
              View all
            </Link>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items === null && <p className="px-3 py-6 text-center text-xs text-muted-foreground">Loading payments…</p>}
            {items !== null && items.length === 0 && (
              <p className="px-3 py-6 text-center text-xs text-muted-foreground">No recent payments recorded.</p>
            )}
            {(items ?? []).map((p) => (
              <div
                key={p.id}
                className="flex items-start gap-3 border-b border-border/40 px-4 py-3 transition last:border-b-0 hover:bg-muted/40"
              >
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-foreground">{p.buyer_name}</p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {p.plan_name} · ₹{Math.round((p.amount_paise || 0) / 100).toLocaleString("en-IN")}
                  </p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {formatDistanceToNow(new Date(p.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
